'use client';

import { useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';

type Translate = (key: string, values?: Record<string, string | number>) => string;
import { GlobalUIContext } from 'contexts/GlobalUIContext';
import { CustomImage as Image } from 'components';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getRingProBasePrice } from 'content/products/ring-pro/tierPricing';
import { RingPriceData, AirRingColors } from 'content/products/ring/productMap/constants';
import { getCurrencySymbolFromEnum } from 'lib/region/countryUtils';
import { formatPriceWithComma } from 'hooks/pricing/useFormattedPrice';
import { DesktopComparison } from './DesktopComparison';
import { MobileComparison } from './MobileComparison';
import { ComparisonGroup } from './comparisonTypes';
import styles from './index.module.css';

const getFormattedPrice = (store: ShopifyStore | null, currency: Currency | null): { airPrice: string; proPrice: string } => {
  const s = store ?? ShopifyStore.ROW;
  const c = currency?.toLowerCase() as Currency ?? Currency.usd;
  const symbol = getCurrencySymbolFromEnum(c);
  const airBase = RingPriceData[s]?.[AirRingColors.RAW_TITANIUM]?.price ?? 349;
  const proBase = getRingProBasePrice(s, c) ?? 479;
  return {
    airPrice: `${symbol}${formatPriceWithComma(airBase, c)}`,
    proPrice: `${symbol}${formatPriceWithComma(proBase, c)}`,
  };
};

const getComparisonData = (
  store: ShopifyStore | null,
  currency: Currency | null,
  tRingPro: Translate,
  tRing: Translate,
): ComparisonGroup[] => {
  const t: Translate = (key) => {
    if (key.startsWith('ring-pro:')) return tRingPro(key.slice('ring-pro:'.length));
    if (key.startsWith('ring:')) return tRing(key.slice('ring:'.length));
    return key;
  };
  const { airPrice, proPrice } = getFormattedPrice(store, currency);
  return [
  {
    heading: t('ring-pro:ring-pro.comparison.specs.heading'),
    description: t('ring-pro:ring-pro.comparison.specs.description'),
    rows: [
      {
        label: '',
        ringPro: {
          type: 'text',
          value: t('ring-pro:ring-pro.comparison.intro.pro'),
        },
        ringAir: {
          type: 'text',
          value: t('ring-pro:ring-pro.comparison.intro.air'),
        },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.price'),
        ringAir: { type: 'text', value: airPrice },
        ringPro: { type: 'text', value: proPrice },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.materials'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.materials.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.materials.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.innerSurface'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.innerSurface.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.innerSurface.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.thickness'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.thickness.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.thickness.pro') },
      },
      {
        label: t('ring:ring.WEIGHT'),
        ringAir: { type: 'text', value: '2.4 g' },
        ringPro: { type: 'text', value: '3.3–4.8 g' },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.waterResistance'),
        ringAir: { type: 'text', value: '100m / 328 ft' },
        ringPro: { type: 'text', value: '100m / 328 ft' },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.sizes'),
        ringAir: { type: 'text', value: '5–14' },
        ringPro: { type: 'text', value: '5–14' },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.colors'),
        ringAir: { type: 'text', value: 'Raw Titanium, Aster Black, Matte Grey, Bionic Gold, Space Silver' },
        ringPro: { type: 'text', value: 'Bionic Gold, Space Silver, Aster Black, Raw Titanium' },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.batteryLife'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.batteryLife.air') },
        ringPro: {
          type: 'text',
          value: t('ring-pro:ring-pro.comparison.value.batteryLife.pro'),
        },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.processor'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.processor.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.processor.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.heartRateSensor'),
        ringAir: { type: 'text', value: 'PPG' },
        ringPro: {
          type: 'text',
          value: t('ring-pro:ring-pro.comparison.value.heartRateSensor.pro'),
        },
      },
      {
        label: t('ring:ring.SENSORS'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.sensors.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.sensors.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.onDeviceStorage'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.onDeviceStorage.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.onDeviceStorage.pro') },
      },
      {
        label: t('ring:ring.CONNECTIVITY'),
        ringAir: { type: 'text', value: 'Bluetooth Low Energy' },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.connectivity.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.safety'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.notApplicable') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.safety.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.calibration'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.calibration.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.calibration.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.charger'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.charger.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.charger.pro') },
      },
    ],
  },
  {
    heading: '',
    description: '',
    columnHeaders: {
      proLabel: t('ring-pro:ring-pro.comparison.columnHeaders.pro'),
      airLabel: t('ring-pro:ring-pro.comparison.columnHeaders.air'),
    },
    rows: [
      {
        label: t('ring-pro:ring-pro.comparison.row.formFactor'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.formFactor.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.formFactor.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.chargingTechnology'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.chargingTechnology.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.chargingTechnology.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.caseBattery'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.notApplicable') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.caseBattery.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.dataBackup'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.notApplicable') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.dataBackup.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.findMyCase'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.notApplicable') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.findMyCase.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.feedback'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.notApplicable') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.feedback.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.firmwareUpdates'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.firmwareUpdates.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.firmwareUpdates.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.caseCharging'),
        ringAir: { type: 'text', value: 'USB-C' },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.caseCharging.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.materials'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.caseMaterials.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.caseMaterials.pro') },
      },
      {
        label: t('ring-pro:ring-pro.comparison.row.safety'),
        ringAir: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.caseSafety.air') },
        ringPro: { type: 'text', value: t('ring-pro:ring-pro.comparison.value.caseSafety.pro') },
      },
    ],
  },
];
};

const ComparisonSection = ({
  className,
  htmlId,
  hideTitle = false,
}: {
  className?: string;
  htmlId?: string;
  hideTitle?: boolean;
}) => {
  const tRingPro = useTranslations('ring-pro');
  const tRing = useTranslations('ring');
  const globalUI = useContext(GlobalUIContext);
  const { store, currency } = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const comparisonData = useMemo(
    () => getComparisonData(store, currency, tRingPro, tRing),
    [store, currency, tRingPro, tRing],
  );

  return (
    <section className={className ? `${styles.root} ${className}` : styles.root} id={htmlId}>
      <div className={styles.comparisonInner}>
        {!hideTitle && <h2 className={styles.comparisonTitle}>{tRingPro('ring-pro.comparison.title')}</h2>}

        <div className={styles.tableWrapper}>
          <div className={styles.proColumnBg} />

        <div className={styles.headerImagesRow}>
          <div className={styles.headerLabel} />
          <div className={styles.headerImageCell}>
            <div className={styles.headerThumb}>
              <Image
                src="web_v2/ring-pro/comparison/ring-pro-thumb.png"
                width={64}
                height={64}
                alt="Ultrahuman Ring PRO"
              />
            </div>
          </div>
          <div className={styles.headerImageCell}>
            <div className={styles.headerThumb}>
              <Image
                src="web_v2/ring-pro/comparison/ring-air-thumb.png"
                width={64}
                height={64}
                alt="Ultrahuman Ring AIR"
              />
            </div>
          </div>
        </div>

        <div className={styles.headerTitlesRow} style={{ top: `${globalUI.headerVisible ? globalUI.headerHeight : 0}px` }}>
          <div className={styles.headerLabel} />
          <div className={`${styles.headerProduct} ${styles.headerProductPro}`}>
            Ring PRO
          </div>
          <div className={styles.headerProduct}>
            Ring AIR
          </div>
        </div>

        {/* both rendered, CSS-toggled: useWindowSize() is 0 on SSR and paints the wrong branch */}
        <MobileComparison className={styles.comparisonMobile} data={comparisonData} />
        <DesktopComparison className={styles.comparisonDesktop} data={comparisonData} />
        </div>
        <p className={styles.comparisonDisclaimer}>
          {tRingPro('ring-pro.comparison.disclaimer')}
        </p>
      </div>
    </section>
  );
};

export default ComparisonSection;
